import { useEffect, useRef, useCallback } from 'react';
import { GOOGLE_CLIENT_ID } from '@/constants/integrations/firebase';
import { useUser } from '@/hooks';
import { logger } from '@/utils';
import { LOGGER_FEATURE, LOGGER_ACTION } from '@/constants/logger/logger-tags';

export const useGoogleOneTap = (): void => {
  const { loginWithGoogleOneTap, isLogin } = useUser();
  const initializedRef = useRef(false);
  const isLoggedIn = isLogin();

  const handleCredentialResponse = useCallback(
    (res: google.accounts.id.CredentialResponse): void => {
      if (res?.credential) {
        loginWithGoogleOneTap(res.credential).finally();
      }
    },
    [loginWithGoogleOneTap]
  );

  useEffect(() => {
    if (!isLoggedIn && !initializedRef.current) {
      try {
        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          auto_select: false,
          context: 'use',
          use_fedcm_for_prompt: true,
        });

        google.accounts.id.prompt(); // triggers navigator.credentials.get()
        initializedRef.current = true;
      } catch (error) {
        logger.error('Failed to initialize Google One Tap', error, {
          tags: {
            feature: LOGGER_FEATURE.AUTHENTICATION,
            action: LOGGER_ACTION.GOOGLE_ONE_TAP_INIT,
          },
        });
      }
    } else if (isLoggedIn) {
      google?.accounts?.id?.cancel(); // cancel One Tap if already logged in
    }
  }, [isLoggedIn, handleCredentialResponse]);
};
